import { Message } from '@arco-design/web-vue'

/**
 * 点击复制指令 v-copy
 * 
 * 使用方式：
 * 1. 基础用法：<div v-copy="'要复制的文本'">点击复制</div>
 * 2. 对象配置：<div v-copy="{ text: '要复制的文本', success: '复制成功提示', error: '复制失败提示' }">点击复制</div>
 * 3. 函数配置：<div v-copy="() => '动态文本'">点击复制</div>
 * 4. 禁用提示：<div v-copy="{ text: '文本', silent: true }">点击复制</div>
 */

// 复制成功回调
function clipboardSuccess(text, customMessage) {
  const message = customMessage || `复制成功`
  Message.success(message)
}

// 复制失败回调
function clipboardError(text, customMessage) {
  const message = customMessage || `复制失败`
  Message.error(message)
}

// 使用现代 Clipboard API 进行复制
async function copyWithClipboardAPI(text, successMsg, errorMsg, silent) {
  try {
    await navigator.clipboard.writeText(text)
    if (!silent) {
      clipboardSuccess(text, successMsg)
    }
    return true
  } catch (err) {
    console.warn('Clipboard API 复制失败，尝试使用 fallback 方法:', err)
    return false
  }
}

// 使用 fallback 方法进行复制（兼容旧浏览器）
function copyWithFallback(text, successMsg, errorMsg, silent) {
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.position = 'fixed'
  textArea.style.left = '-999999px'
  textArea.style.top = '-999999px'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()
  
  try {
    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)
    
    if (successful) {
      if (!silent) {
        clipboardSuccess(text, successMsg)
      }
      return true
    } else {
      if (!silent) {
        clipboardError(text, errorMsg)
      }
      return false
    }
  } catch (err) {
    document.body.removeChild(textArea)
    if (!silent) {
      clipboardError(text, errorMsg)
    }
    return false
  }
}

// 主复制函数
async function handleCopy(text, successMsg, errorMsg, silent = false) {
  // 检查是否支持 Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    const success = await copyWithClipboardAPI(text, successMsg, errorMsg, silent)
    if (success) return
  }
  
  // 使用 fallback 方法
  copyWithFallback(text, successMsg, errorMsg, silent)
}

// 解析指令绑定值
function parseBindingValue(binding) {
  const value = binding.value
  
  if (!value) {
    console.warn('v-copy 指令需要绑定值')
    return null
  }
  
  // 如果是函数，执行函数获取文本
  if (typeof value === 'function') {
    try {
      const text = value()
      return {
        text: String(text || ''),
        success: null,
        error: null,
        silent: false
      }
    } catch (err) {
      console.error('v-copy 指令绑定的函数执行失败:', err)
      return null
    }
  }
  
  // 如果是字符串，直接使用
  if (typeof value === 'string' || typeof value === 'number') {
    return {
      text: String(value),
      success: null,
      error: null,
      silent: false
    }
  }
  
  // 如果是对象，解析配置
  if (typeof value === 'object' && value !== null) {
    return {
      text: String(value.text || ''),
      success: value.success || null,
      error: value.error || null,
      silent: Boolean(value.silent)
    }
  }
  
  console.warn('v-copy 指令绑定值类型不支持:', typeof value)
  return null
}

// 点击事件处理器
function createClickHandler(binding) {
  return async function(event) {
    event.preventDefault()
    
    const config = parseBindingValue(binding)
    if (!config) return
    
    if (!config.text) {
      console.warn('v-copy 指令没有要复制的文本')
      return
    }
    
    await handleCopy(config.text, config.success, config.error, config.silent)
  }
}

export default {
  // Vue 3 生命周期：元素挂载时
  mounted(el, binding) {
    // 添加复制样式提示
    el.style.cursor = 'pointer'
    el.title = el.title || '点击复制'
    
    // 创建点击事件处理器
    const clickHandler = createClickHandler(binding)
    
    // 绑定点击事件
    el.addEventListener('click', clickHandler)
    
    // 将处理器存储到元素上，以便后续移除
    el._copyClickHandler = clickHandler
  },
  
  // Vue 3 生命周期：指令绑定值更新时
  updated(el, binding) {
    // 移除旧的事件监听器
    if (el._copyClickHandler) {
      el.removeEventListener('click', el._copyClickHandler)
    }
    
    // 创建新的点击事件处理器
    const clickHandler = createClickHandler(binding)
    
    // 绑定新的点击事件
    el.addEventListener('click', clickHandler)
    
    // 更新存储的处理器
    el._copyClickHandler = clickHandler
  },
  
  // Vue 3 生命周期：元素卸载时
  unmounted(el) {
    // 移除事件监听器
    if (el._copyClickHandler) {
      el.removeEventListener('click', el._copyClickHandler)
      delete el._copyClickHandler
    }
    
    // 清理样式
    el.style.cursor = ''
    if (el.title === '点击复制') {
      el.title = ''
    }
  }
}